'use client';
import React, { useContext, createContext, useReducer, useEffect } from 'react';

import { LoyaltyType } from '@api/index';

import {
  Action,
  HotelDetailState,
  ACTIONS,
  SelectedVariationProps,
} from './types';

const initialValues = {
  basketId: '',
  hasModal: null,
  selectedVariations: [],
};

type HotelDetailDispatch = (action: Action) => void;

const HotelDetailStateContext = createContext<HotelDetailState | undefined>(
  undefined
);
const HotelDetailDispatchContext = createContext<
  HotelDetailDispatch | undefined
>(undefined);

const hotelDetailReducer = (
  state: HotelDetailState,
  action: Action
): HotelDetailState => {
  switch (action.type) {
    case ACTIONS.SET_BASKET_ID:
      return { ...state, basketId: action.payload };
    case ACTIONS.SET_MODAL:
      return { ...state, loyaltyType: action.payload };
    case ACTIONS.SET_VARIATIONS:
      return { ...state, selectedVariations: action.payload };
    case ACTIONS.SET_UNSELECTED_ROOMS:
      return { ...state, unSelectedRooms: action.payload };
    case ACTIONS.CLEAR:
      return initialValues;
    default:
      return state;
  }
};

const HotelDetailProvider = ({
  children,
}: React.PropsWithChildren<unknown>) => {
  const [state, dispatch] = useReducer(hotelDetailReducer, initialValues);
  const basketId =
    typeof window !== 'undefined' && localStorage.getItem('basketId');

  useEffect(() => {
    if (basketId) {
      dispatch({ type: ACTIONS.SET_BASKET_ID, payload: basketId });
    }
  }, [basketId]);

  return (
    <HotelDetailStateContext.Provider value={state}>
      <HotelDetailDispatchContext.Provider value={dispatch}>
        {children}
      </HotelDetailDispatchContext.Provider>
    </HotelDetailStateContext.Provider>
  );
};

export const useHotelDetailState = (): HotelDetailState => {
  const context = useContext(HotelDetailStateContext);
  if (context === undefined) {
    throw new Error(
      'useHotelDetailState must be used within a HotelDetailProvider'
    );
  }
  return context;
};

export const useSetBasketId = (): ((payload: string) => void) => {
  const dispatch = useContext(HotelDetailDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetBasketId must be used within a HotelDetailProvider');
  }
  return (payload: string) =>
    dispatch({ type: ACTIONS.SET_BASKET_ID, payload: payload });
};

export const useSetModalLoyaltyType = (): ((
  payload: LoyaltyType | null
) => void) => {
  const dispatch = useContext(HotelDetailDispatchContext);
  if (dispatch === undefined) {
    throw new Error(
      'useSetModalDispatch must be used within a HotelDetailProvider'
    );
  }
  return (payload: LoyaltyType | null) => {
    dispatch({ type: ACTIONS.SET_MODAL, payload: payload });
  };
};

export const useSetSelectedVariations = (): ((
  payload: SelectedVariationProps[]
) => void) => {
  const dispatch = useContext(HotelDetailDispatchContext);
  if (dispatch === undefined) {
    throw new Error(
      'useSetSelectedVariations must be used within a HotelDetailProvider'
    );
  }
  return (payload: SelectedVariationProps[]) => {
    dispatch({ type: ACTIONS.SET_VARIATIONS, payload: payload });
  };
};

export const useSetUnSelectedRooms = (): ((payload: number[]) => void) => {
  const dispatch = useContext(HotelDetailDispatchContext);
  if (dispatch === undefined) {
    throw new Error(
      'useSetUnSelectedRooms must be used within a HotelDetailProvider'
    );
  }
  return (payload: number[]) => {
    dispatch({ type: ACTIONS.SET_UNSELECTED_ROOMS, payload: payload });
  };
};

export const useClearDispatch = (): (() => void) => {
  const dispatch = useContext(HotelDetailDispatchContext);
  if (dispatch === undefined) {
    throw new Error(
      'useClearDispatch must be used within a HotelDetailProvider'
    );
  }
  return () => dispatch({ type: ACTIONS.CLEAR });
};

export default HotelDetailProvider;
